import { useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ProgramCardShimmer } from "src/components/program/conductor/program.cad.shimmer";
import { Reload } from "src/components/svg";
import { ProgramType } from "src/enums";
import { UIService, _ } from "src/services";
import { ArchiveList as ReduxArchiveList, ArchiveListAction } from "src/store";
import { ArchiveDayCard } from "./archive.day.card";
import { ArchiveListShimmer } from "./archive.list.shimmer";

export const ArchiveList: React.FC = () => {

    //#region Dependencies

    const dispatch = useDispatch();
    const list: ReduxArchiveList = useSelector((state: any) => state.ArchiveListState);

    //#endregion

    //#region properties

    const moreRef = useRef<HTMLDivElement>(null);
    const archiveLoading = useRef<boolean>(false);

    const programCount = list.list.reduce((x, y) => x + y.items.filter((a) => a.program).length, 0);

    //#endregion

    //#region mehtods

    const fetchData = useCallback((clear: boolean, clearBeforeFetch?: boolean) => {

        if (list.loading || (archiveLoading && archiveLoading.current)) return;

        archiveLoading.current = true;

        const offset = clear ? 0 : list.offset;

        dispatch(ArchiveListAction.getData(list.count, ProgramType.ArchiveVideo, offset, clear ? "" : list.searchText, clear, clearBeforeFetch));
    }, [dispatch, list]);

    const loadMore = useCallback(() => fetchData(false), [fetchData]);

    const reload = useCallback((clearBeforeFetch?: boolean) => {
        dispatch(ArchiveListAction.setSearchText(""));
        fetchData(true, clearBeforeFetch);
    }, [fetchData, dispatch]);

    const handleScroll = useCallback((e: Event) => {

        if (!list.list.length || !list.hasMore || list.offset > 4 * list.count || list.loading) return;


        const isInView = UIService.isInView(moreRef);

        if (isInView) loadMore();

    }, [list, loadMore]);

    //#endregion

    //#region effects

    useEffect(() => {
        reload();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {

        document.addEventListener("scroll", handleScroll);
        document.addEventListener("touchmove", handleScroll, false);

        return (() => {
            document.removeEventListener("scroll", handleScroll);
            document.removeEventListener("touchmove", handleScroll, false);
        });

    }, [handleScroll]);


    useEffect(() => {

        if (!list.loading && archiveLoading && archiveLoading.current)
            archiveLoading.current = false;

    }, [list.loading]);


    //#endregion

    //#region  helpers

    const renderLoadMore = () => {

        if (!list.hasMore || list.totalRecords <= programCount || list.offset >= list.totalRecords) return null;

        return (
            <div className="db-blcok h-4" ref={moreRef}>
                {
                    list.list.length > 0 && !list.loading &&
                    <button onClick={loadMore} className="db-block btn btn-link load-more">{_("more")}</button>
                }
                {
                    list.list.length > 0 && list.loading &&
                    [...new Array(2)].map((x, index) => (<ProgramCardShimmer key={index} />))
                }
            </div>
        );
    };

    const renderTitle = () => {

        return (
            <div className="archive-list-area mt-4">
                <div className="clearfix">
                    <h5 className="float-right text-rtl text-right section-title">{_("archiveList")}</h5>
                    <button title={_("reload")} type="button" onClick={() => reload(false)}
                        className={`float-left btn btn-sm button-dark btn-refresh btn-secondary text-rtl${list.loading ? " btn-loading" : ""}`}>
                        <span className="icon">
                            <Reload />
                        </span>
                        <span className="text">{_("reload")}</span>
                    </button>
                </div>
            </div >
        );
    };

    //#endregion

    const showShimmer = list.firstLoad || (!list.list.length && list.loading);
    const showEmpty = !list.firstLoad && !list.list.length && !list.loading;

    return (

        <div className="container p-sm-0 text-center">
            {renderTitle()}
            {showShimmer && <ArchiveListShimmer />}
            {
                showEmpty &&
                <div className="row">
                    <div className="col-12 text-center text-rtl">
                        <h6 className="no-result-title mt-4 pt-4">
                            {list.searchText ? _("noSearchLResult") : _("archiveUpdating")}
                        </h6>
                    </div>
                </div>
            }
            {
                list.list.filter((day) => day.items.length).map((day, index) => (
                    <ArchiveDayCard key={index} {...day} />
                ))
            }
            {renderLoadMore()}
        </div>

    );

};